// Incident Registry — Fase 15.4
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical'
export type IncidentStatus   = 'open' | 'investigating' | 'mitigated' | 'resolved' | 'postmortem'

export interface IncidentEvent {
  timestamp: string; type: 'opened'|'updated'|'escalated'|'mitigated'|'resolved'|'note'
  message: string; actor: string
}
export interface Incident {
  id: string; title: string; description: string; severity: IncidentSeverity
  status: IncidentStatus; domain: string; companyId: string | null; openedAt: string
  updatedAt: string; resolvedAt: string | null; timeline: IncidentEvent[]
  affectedSystems: string[]; autoEscalated: boolean
}
export interface IncidentSummary {
  timestamp: string; total: number; open: number; critical: number; high: number
  avgResolutionMs: number | null
  recentIncidents: Array<{ id: string; title: string; severity: IncidentSeverity; status: IncidentStatus }>
}

const ESCALATION_MS: Record<IncidentSeverity, number> = {
  low: 4*3600_000, medium: 2*3600_000, high: 30*60_000, critical: 10*60_000
}
let _incidents: Incident[] = []
let _timer: ReturnType<typeof setInterval> | null = null
function genId(): string { return `inc_${Date.now()}_${Math.random().toString(36).slice(2,8)}` }

export function openIncident(p: { title: string; description: string; severity: IncidentSeverity; domain: string; companyId?: string; affectedSystems?: string[] }): Incident {
  const now = new Date().toISOString()
  const inc: Incident = { id: genId(), title: p.title, description: p.description, severity: p.severity,
    status: 'open', domain: p.domain, companyId: p.companyId ?? null, openedAt: now, updatedAt: now,
    resolvedAt: null, timeline: [{ timestamp: now, type: 'opened', message: `Opened: ${p.title}`, actor: 'system' }],
    affectedSystems: p.affectedSystems ?? [], autoEscalated: false }
  _incidents.push(inc); return inc
}
export function addIncidentNote(id: string, message: string, actor = 'system'): void {
  const inc = _incidents.find(i => i.id === id); if (!inc) return
  inc.timeline.push({ timestamp: new Date().toISOString(), type: 'note', message, actor })
  inc.updatedAt = new Date().toISOString()
}
export function updateIncidentStatus(id: string, status: IncidentStatus, message?: string, actor = 'system'): void {
  const inc = _incidents.find(i => i.id === id); if (!inc) return
  const now = new Date().toISOString()
  const type = status === 'resolved' ? 'resolved' : status === 'mitigated' ? 'mitigated' : 'updated'
  inc.status = status; inc.updatedAt = now
  if (status === 'resolved') inc.resolvedAt = now
  inc.timeline.push({ timestamp: now, type, message: message ?? `Status: ${status}`, actor })
}
export function escalateIncident(id: string): void {
  const inc = _incidents.find(i => i.id === id); if (!inc || inc.status === 'resolved') return
  const next: Record<IncidentSeverity, IncidentSeverity> = { low:'medium', medium:'high', high:'critical', critical:'critical' }
  inc.severity = next[inc.severity]; inc.autoEscalated = true; inc.updatedAt = new Date().toISOString()
  inc.timeline.push({ timestamp: inc.updatedAt, type: 'escalated', message: `Auto-escalated to ${inc.severity}`, actor: 'system' })
}
export function runAutoEscalationCheck(): void {
  const now = Date.now()
  for (const inc of _incidents) {
    if (inc.status === 'resolved' || inc.status === 'mitigated' || inc.autoEscalated) continue
    if (now - new Date(inc.openedAt).getTime() > ESCALATION_MS[inc.severity]) escalateIncident(inc.id)
  }
}
export function startEscalationMonitor(intervalMs = 60_000): () => void {
  if (_timer) clearInterval(_timer)
  _timer = setInterval(runAutoEscalationCheck, intervalMs)
  return () => { if (_timer) { clearInterval(_timer); _timer = null } }
}
export function getIncidents(): Incident[]       { return [..._incidents] }
export function getOpenIncidents(): Incident[]   { return _incidents.filter(i => i.status !== 'resolved') }
export function getCriticalIncidents(): Incident[]{ return _incidents.filter(i => i.severity === 'critical' && i.status !== 'resolved') }
export function buildIncidentSummary(): IncidentSummary {
  const resolved = _incidents.filter(i => i.resolvedAt)
  const avgRes   = resolved.length > 0
    ? resolved.reduce((s, i) => s + (new Date(i.resolvedAt!).getTime() - new Date(i.openedAt).getTime()), 0) / resolved.length
    : null
  return {
    timestamp: new Date().toISOString(), total: _incidents.length,
    open:     _incidents.filter(i => i.status !== 'resolved').length,
    critical: _incidents.filter(i => i.severity === 'critical' && i.status !== 'resolved').length,
    high:     _incidents.filter(i => i.severity === 'high'     && i.status !== 'resolved').length,
    avgResolutionMs: avgRes ? Math.round(avgRes) : null,
    recentIncidents: [..._incidents].sort((a,b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0,5).map(i => ({ id: i.id, title: i.title, severity: i.severity, status: i.status })),
  }
}
export function resetIncidentRegistry(): void { _incidents = [] }
